import { useMemo, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import { get, put } from '@/api/client';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Input } from '@/components/atoms/Input';
import { Button } from '@/components/atoms/Button';
import { AlertTriangle, UserPlus, Users, FileText, DollarSign, Lock, Eye, CalendarDays, CalendarRange, CalendarClock, RefreshCw, CreditCard, Pencil, ExternalLink } from 'lucide-react';

/** USD → KRW 환산 범위 — 환율 변동 흡수용 lo/hi (1300~1450). */
const KRW_LO = 1300;
const KRW_HI = 1450;
function usdToKrwRange(usd: number): string {
  if (!usd || usd <= 0) return '0원';
  const lo = Math.round(usd * KRW_LO);
  const hi = Math.round(usd * KRW_HI);
  if (lo === hi) return `${lo.toLocaleString()}원`;
  return `${lo.toLocaleString()}~${hi.toLocaleString()}원`;
}

interface Overview {
  pendingReports: number;
  newUsersToday: number;
  dau: number;
  postsToday: number;
  aiCostToday: number;
  aiCostWeek: number;
  aiCostMonth: number;
  aiCostYear: number;
  totalUsers: number;
  totalPosts: number;
  pendingExchanges: number;
}

interface AiCostPeriod {
  type: 'month' | 'year';
  value: string;
  costUsd: number;
}

/** 서비스 시작 연도 — 년 필터 드롭다운 시작점 */
const YEAR_START = 2026;

interface RecentExchange {
  id: number;
  item_name: string;
  point_amount: number;
  status: string;
  nickname: string;
  created_at: string;
}

interface Charts {
  signups: Array<{ date: string; count: number }>;
  dau: Array<{ date: string; count: number }>;
  posts: Array<{ date: string; count: number }>;
  aiCost: Array<{ date: string; cost_usd: number; calls: number }>;
}

interface Suggestion {
  id: number;
  title: string;
  is_anonymous: boolean;
  is_private: boolean;
  is_blinded: boolean;
  comment_count: number;
  created_at: string;
  author_nickname: string | null;
}

function StatCard({
  label,
  value,
  icon: Icon,
  to,
  warn,
  subValue,
}: {
  label: string;
  value: string | number;
  icon: typeof AlertTriangle;
  to?: string;
  warn?: boolean;
  /** 보조 표시(예: USD 옆 KRW 환산) */
  subValue?: string;
}) {
  const inner = (
    <div
      className={`rounded-xl border p-4 ${
        warn
          ? 'border-[var(--color-rise)] bg-[var(--color-rise)]/5'
          : 'border-[var(--color-border)] bg-[var(--color-surface)]'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs text-[var(--color-text-sub)]">{label}</span>
        <Icon className="h-4 w-4 text-[var(--color-text-sub)]" />
      </div>
      <p
        className={`mt-2 text-2xl font-bold ${
          warn ? 'text-[var(--color-rise)]' : 'text-[var(--color-text)]'
        }`}
      >
        {value}
      </p>
      {subValue && (
        <p className="mt-0.5 text-[11px] text-[var(--color-text-sub)]">{subValue}</p>
      )}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

/**
 * AI 비용 카드 (오늘/이번 주/월별/년별 공용) — 4 카드 높이 자동 정렬.
 *
 * 모든 카드가 동일 구조(label row → period row → value row → krw row)를 갖고
 * `h-full + flex flex-col` 로 grid 행 높이에 자동 stretch. period row 영역은
 * 드롭다운(월/년) 또는 정적 배지(오늘/이번 주)로 채워 빈 줄로 두지 않음.
 */
function PeriodCostCard({
  label,
  icon: Icon,
  costUsd,
  periodSlot,
  usdToKrwRange,
  to,
}: {
  label: string;
  icon: typeof AlertTriangle;
  costUsd: number;
  /** 기간 표시 영역 — select 컴포넌트들 또는 정적 배지(오늘/이번 주) */
  periodSlot: React.ReactNode;
  usdToKrwRange: (usd: number) => string;
  to?: string;
}) {
  const inner = (
    <div className="flex h-full flex-col rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs text-[var(--color-text-sub)]">{label}</span>
        <Icon className="h-4 w-4 text-[var(--color-text-sub)]" />
      </div>
      {/* 기간 행 — 드롭다운 또는 정적 배지. min-height 28px (select h-7) 로 고정해 4 카드 정렬. */}
      <div className="mb-2 flex min-h-[28px] flex-wrap items-center gap-1.5">
        {periodSlot}
      </div>
      {/* 값/환산 — mt-auto 로 카드 하단 정렬 (동일 baseline) */}
      <p className="mt-auto text-2xl font-bold text-[var(--color-text)]">
        ${costUsd.toFixed(4)}
      </p>
      <p className="mt-0.5 text-[11px] text-[var(--color-text-sub)]">
        약 {usdToKrwRange(costUsd)}
      </p>
    </div>
  );
  return to ? (
    <Link to={to} className="block h-full">
      {inner}
    </Link>
  ) : (
    inner
  );
}

/** 정적 기간 배지 — "오늘" / "이번 주" 처럼 드롭다운 없는 카드의 period 자리에 표시 */
function StaticPeriodBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex h-7 items-center rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 text-xs font-medium text-[var(--color-text-sub)]">
      {children}
    </span>
  );
}

/** Anthropic 크레딧 — 자체 추정 (baseline USD - baseline 이후 누적 AI 비용) */
interface AnthropicCredits {
  baselineUsd: number;
  baselineAt: string;
  usedSinceBaselineUsd: number;
  callsSinceBaseline: number;
  remainingUsd: number;
  warn: boolean;
  cachedAt?: number;
}

export function DashboardPage() {
  const qc = useQueryClient();
  const { data: overview, isLoading: ovLoading } = useQuery({
    queryKey: ['admin', 'dashboard', 'overview'],
    queryFn: () => get<Overview>('/admin/dashboard/overview'),
  });

  /** Anthropic 크레딧 — 5분 staleTime, 수동 새로고침 가능 */
  const creditsQuery = useQuery<AnthropicCredits, Error>({
    queryKey: ['admin', 'anthropic-credits'],
    queryFn: () => get<AnthropicCredits>('/admin/system/anthropic-credits'),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const { data: charts, isLoading: chLoading } = useQuery({
    queryKey: ['admin', 'dashboard', 'charts', 30],
    queryFn: () => get<Charts>('/admin/dashboard/charts?days=30'),
  });
  const { data: suggestions, isLoading: sgLoading } = useQuery({
    queryKey: ['admin', 'dashboard', 'recent-suggestions'],
    queryFn: () => get<{ suggestions: Suggestion[] }>('/admin/dashboard/recent-suggestions'),
  });
  const { data: exchanges, isLoading: exLoading } = useQuery({
    queryKey: ['admin', 'dashboard', 'recent-exchanges'],
    queryFn: () => get<{ exchanges: RecentExchange[] }>('/admin/dashboard/recent-exchanges'),
    refetchInterval: 60_000,
  });

  /* AI 비용 — 월/년 필터 상태. 기본값은 현재 KST 기준 월/년. */
  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const [filterYear, setFilterYear] = useState<number>(currentYear);
  const [filterMonth, setFilterMonth] = useState<string>(
    `${currentYear}-${String(currentMonth).padStart(2, '0')}`,
  );

  /* 월/년 드롭다운 옵션 — 서비스 시작년 ~ 현재년, 월은 1~12 */
  const yearOptions = useMemo(() => {
    const arr: number[] = [];
    for (let y = currentYear; y >= YEAR_START; y--) arr.push(y);
    return arr;
  }, [currentYear]);
  const monthOptions = useMemo(() => {
    /* 선택된 월 필터의 연도 기준으로 1~12월 노출 */
    const [yStr] = filterMonth.split('-');
    const y = Number(yStr);
    const arr: string[] = [];
    for (let m = 1; m <= 12; m++) {
      arr.push(`${y}-${String(m).padStart(2, '0')}`);
    }
    return arr;
  }, [filterMonth]);

  const { data: aiCostMonth } = useQuery({
    queryKey: ['admin', 'dashboard', 'ai-cost', 'month', filterMonth],
    queryFn: () =>
      get<AiCostPeriod>(
        `/admin/dashboard/ai-cost-period?type=month&value=${encodeURIComponent(filterMonth)}`,
      ),
    refetchInterval: 60_000,
  });
  const { data: aiCostYear } = useQuery({
    queryKey: ['admin', 'dashboard', 'ai-cost', 'year', filterYear],
    queryFn: () =>
      get<AiCostPeriod>(`/admin/dashboard/ai-cost-period?type=year&value=${filterYear}`),
    refetchInterval: 60_000,
  });

  return (
    <div className="w-full">
      <h1 className="mb-6 text-2xl font-extrabold">대시보드</h1>

      {/* Anthropic 크레딧 잔액 — 자체 추정 (baseline - 이후 누적 비용). super 권한 전용. */}
      <AnthropicCreditCard
        loading={creditsQuery.isLoading}
        error={creditsQuery.error}
        credits={creditsQuery.data}
        onRefresh={() => qc.invalidateQueries({ queryKey: ['admin', 'anthropic-credits'] })}
        isRefreshing={creditsQuery.isFetching}
      />

      {ovLoading ? (
        <Skeleton className="mb-6 h-24" />
      ) : (
        <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard
            label="미처리 신고"
            value={overview?.pendingReports ?? 0}
            icon={AlertTriangle}
            to="/admin/reports"
            warn={(overview?.pendingReports ?? 0) > 0}
          />
          <StatCard
            label="오늘 가입자"
            value={overview?.newUsersToday ?? 0}
            icon={UserPlus}
          />
          <StatCard label="DAU (오늘 활성)" value={overview?.dau ?? 0} icon={Users} />
          <StatCard
            label="오늘 게시글"
            value={overview?.postsToday ?? 0}
            icon={FileText}
          />
          <StatCard label="누적 유저" value={overview?.totalUsers ?? 0} icon={Users} />
          <StatCard label="누적 게시글" value={overview?.totalPosts ?? 0} icon={FileText} />
        </div>
      )}

      {/* AI 비용 — 오늘/이번 주/월별(필터)/년별(필터) 4분할. 4 카드 높이 통일. */}
      {!ovLoading && (
        <div className="mb-6 grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {/* 오늘 */}
          <PeriodCostCard
            label="오늘 AI 비용"
            icon={DollarSign}
            costUsd={overview?.aiCostToday ?? 0}
            periodSlot={<StaticPeriodBadge>오늘</StaticPeriodBadge>}
            usdToKrwRange={usdToKrwRange}
            to="/admin/system"
          />
          {/* 이번 주 */}
          <PeriodCostCard
            label="이번 주 AI 비용"
            icon={CalendarDays}
            costUsd={overview?.aiCostWeek ?? 0}
            periodSlot={<StaticPeriodBadge>이번 주</StaticPeriodBadge>}
            usdToKrwRange={usdToKrwRange}
            to="/admin/system"
          />
          {/* 월별 (드롭다운 필터) */}
          <PeriodCostCard
            label="월별 AI 비용"
            icon={CalendarRange}
            costUsd={aiCostMonth?.costUsd ?? 0}
            periodSlot={
              <>
                <select
                  value={filterMonth.split('-')[0]}
                  onChange={(e) => {
                    const y = e.target.value;
                    const [, m] = filterMonth.split('-');
                    setFilterMonth(`${y}-${m}`);
                  }}
                  className="h-7 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 text-xs"
                  aria-label="연도 선택"
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
                <select
                  value={filterMonth}
                  onChange={(e) => setFilterMonth(e.target.value)}
                  className="h-7 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 text-xs"
                  aria-label="월 선택"
                >
                  {monthOptions.map((ym) => {
                    const [, m] = ym.split('-');
                    return (
                      <option key={ym} value={ym}>
                        {Number(m)}월
                      </option>
                    );
                  })}
                </select>
              </>
            }
            usdToKrwRange={usdToKrwRange}
          />
          {/* 년별 (드롭다운 필터) */}
          <PeriodCostCard
            label="년도별 AI 비용"
            icon={CalendarClock}
            costUsd={aiCostYear?.costUsd ?? 0}
            periodSlot={
              <select
                value={filterYear}
                onChange={(e) => setFilterYear(Number(e.target.value))}
                className="h-7 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 text-xs"
                aria-label="연도 선택"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}년
                  </option>
                ))}
              </select>
            }
            usdToKrwRange={usdToKrwRange}
          />
        </div>
      )}

      {/* 최근 교환 + 최근 건의/제안 — 2분할 레이아웃 (모바일 1열, lg+ 2열) */}
      <div className="mb-8 grid gap-6 lg:grid-cols-2">
        {/* 최근 교환 신청 */}
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-bold">최근 교환 신청</h2>
            <Link
              to="/admin/exchanges"
              className="text-xs text-[var(--color-primary)] hover:underline"
            >
              전체 보기 →
            </Link>
          </div>
          {exLoading ? (
            <Skeleton className="h-32" />
          ) : (
            <ul className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
              {exchanges?.exchanges.map((e) => (
                <li
                  key={e.id}
                  className="flex items-center gap-2 border-t border-[var(--color-border)] p-3 first:border-t-0"
                >
                  <span className="flex min-w-0 flex-1 items-center gap-2">
                    {e.status === 'pending' ? (
                      <span className="inline-flex shrink-0 items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                        대기
                      </span>
                    ) : e.status === 'approved' ? (
                      <span className="inline-flex shrink-0 items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                        승인
                      </span>
                    ) : e.status === 'rejected' ? (
                      <span className="inline-flex shrink-0 items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700">
                        거절
                      </span>
                    ) : (
                      <span className="inline-flex shrink-0 items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-700">
                        {e.status}
                      </span>
                    )}
                    <span className="truncate text-sm font-medium">{e.item_name}</span>
                    <span className="shrink-0 text-xs text-[var(--color-text-sub)]">
                      {e.point_amount.toLocaleString()}P
                    </span>
                  </span>
                  <span className="shrink-0 text-xs text-[var(--color-text-sub)]">
                    {e.nickname} · {new Date(e.created_at).toISOString().slice(5, 10)}
                  </span>
                </li>
              ))}
              {exchanges?.exchanges.length === 0 && (
                <li className="p-6 text-center text-sm text-[var(--color-text-sub)]">
                  교환 신청이 없어요.
                </li>
              )}
            </ul>
          )}
        </section>

        {/* 최근 건의/제안 */}
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-bold">최근 건의/제안</h2>
            <Link
              to="/admin/posts?boardId=13"
              className="text-xs text-[var(--color-primary)] hover:underline"
            >
              전체 보기 →
            </Link>
          </div>
          {sgLoading ? (
            <Skeleton className="h-40" />
          ) : (
            <ul className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
              {suggestions?.suggestions.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center gap-2 border-t border-[var(--color-border)] p-3 first:border-t-0"
                >
                  <span className="flex min-w-0 flex-1 items-center gap-2">
                    {s.is_private && (
                      <Lock className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-sub)]" aria-label="숨김" />
                    )}
                    {s.is_blinded && (
                      <Eye className="h-3.5 w-3.5 shrink-0 text-[var(--color-fall)]" aria-label="블라인드" />
                    )}
                    <Link
                      to={`/post/${s.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate text-sm font-medium hover:underline"
                    >
                      {s.title}
                    </Link>
                    {s.comment_count > 0 && (
                      <span className="shrink-0 text-xs font-semibold text-[var(--color-primary)]">
                        [{s.comment_count}]
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 text-xs text-[var(--color-text-sub)]">
                    {s.author_nickname ?? '-'} · {new Date(s.created_at).toISOString().slice(5, 10)}
                  </span>
                </li>
              ))}
              {suggestions?.suggestions.length === 0 && (
                <li className="p-6 text-center text-sm text-[var(--color-text-sub)]">
                  건의/제안이 없어요.
                </li>
              )}
            </ul>
          )}
        </section>
      </div>

      <h2 className="mb-3 text-lg font-bold">최근 30일 추이</h2>
      {chLoading ? (
        <Skeleton className="h-72" />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <ChartCard title="가입자" data={charts?.signups ?? []} dataKey="count" color="#1d4ed8" />
          <ChartCard title="DAU" data={charts?.dau ?? []} dataKey="count" color="#16a34a" />
          <ChartCard title="게시글" data={charts?.posts ?? []} dataKey="count" color="#9333ea" />
          <ChartCard
            title="AI 비용 (USD)"
            data={charts?.aiCost ?? []}
            dataKey="cost_usd"
            color="#ea580c"
          />
        </div>
      )}
    </div>
  );
}

/** Anthropic API 크레딧 잔액 — 자체 추정 카드.
 *  baselineUsd (운영자 입력) - 누적 AI 비용 = remainingUsd.
 *  콘솔에서 새 충전 후 "기준 잔액 업데이트" 로 baseline 재설정. */
function AnthropicCreditCard({
  loading,
  error,
  credits,
  onRefresh,
  isRefreshing,
}: {
  loading: boolean;
  error: Error | null;
  credits: AnthropicCredits | undefined;
  onRefresh: () => void;
  isRefreshing: boolean;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<{ open: boolean; value: string }>(
    { open: false, value: '' },
  );

  const updateMut = useMutation({
    mutationFn: (baselineUsd: number) =>
      put<{ baselineUsd: number; baselineAt: string }>(
        '/admin/system/anthropic-credits/baseline',
        { baselineUsd },
      ),
    onSuccess: () => {
      setEditing({ open: false, value: '' });
      qc.invalidateQueries({ queryKey: ['admin', 'anthropic-credits'] });
    },
  });

  const warn = credits?.warn === true;
  const remaining = credits?.remainingUsd;
  const used = credits?.usedSinceBaselineUsd ?? 0;
  const baseline = credits?.baselineUsd;
  const usagePct = baseline && baseline > 0 ? Math.min(100, (used / baseline) * 100) : 0;

  // baseline 시각 — KST 표시
  const baselineAtKst = credits?.baselineAt
    ? new Date(credits.baselineAt).toLocaleString('ko-KR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      })
    : null;

  return (
    <section
      className={`mb-6 rounded-xl border p-4 ${
        warn
          ? 'border-[var(--color-rise)] bg-[var(--color-rise)]/5'
          : 'border-[var(--color-border)] bg-[var(--color-surface)]'
      }`}
    >
      {/* 상단 — 라벨 + 액션 버튼 */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <CreditCard
            className={`h-5 w-5 ${warn ? 'text-[var(--color-rise)]' : 'text-[var(--color-text-sub)]'}`}
          />
          <span className="text-sm font-semibold">Anthropic 크레딧 잔액</span>
          <span className="rounded-md bg-[var(--color-bg)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--color-text-sub)]">
            자체 추정
          </span>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          {/* Anthropic Console — 새 탭으로 결제/잔액 페이지 이동 */}
          <a
            href="https://console.anthropic.com/settings/billing"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-1.5 text-xs font-semibold text-[var(--color-text-sub)] hover:text-[var(--color-text)]"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Anthropic Console
          </a>
          <button
            type="button"
            onClick={() => setEditing({ open: true, value: baseline ? String(baseline) : '' })}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-1.5 text-xs font-semibold text-[var(--color-text-sub)] hover:text-[var(--color-text)]"
          >
            <Pencil className="h-3.5 w-3.5" />
            기준 잔액 업데이트
          </button>
          <button
            type="button"
            onClick={onRefresh}
            disabled={isRefreshing}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-1.5 text-xs font-semibold text-[var(--color-text-sub)] hover:text-[var(--color-text)] disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            새로고침
          </button>
        </div>
      </div>

      {/* 본문 — 잔액 / 진행률 / 보조 정보 */}
      {loading ? (
        <Skeleton className="h-12 w-40" />
      ) : error ? (
        <div className="space-y-2">
          <p className="text-sm text-[var(--color-fall)]">조회 실패 — {error.message}</p>
          <p className="text-xs text-[var(--color-text-sub)]">
            "기준 잔액 업데이트" 로 콘솔에서 확인한 현재 잔액을 입력해주세요.
          </p>
        </div>
      ) : credits ? (
        <div className="space-y-2">
          <div className="flex flex-wrap items-baseline gap-2">
            <span
              className={`text-3xl font-extrabold tabular-nums ${
                warn ? 'text-[var(--color-rise)]' : 'text-[var(--color-text)]'
              }`}
            >
              ${remaining!.toFixed(2)}
            </span>
            <span className="text-xs text-[var(--color-text-sub)]">
              / ${baseline!.toFixed(2)} 기준
            </span>
            {warn && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-rise)]/10 px-2 py-0.5 text-xs font-bold text-[var(--color-rise)]">
                <AlertTriangle className="h-3 w-3" />
                잔액 부족 — $10 이하
              </span>
            )}
          </div>
          {/* 사용량 진행률 */}
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-border)]">
            <div
              className={`h-full ${
                warn ? 'bg-[var(--color-rise)]' : 'bg-[var(--color-primary)]'
              }`}
              style={{ width: `${usagePct.toFixed(1)}%` }}
            />
          </div>
          {/* 보조 정보 — baseline 시각 / 사용량 / 호출수 / 콘솔 로그인 ID */}
          <p className="text-[11px] text-[var(--color-text-sub)]">
            {baselineAtKst} 기준 이후 ${used.toFixed(4)} 사용 · {credits.callsSinceBaseline.toLocaleString()} 회 호출
          </p>
          <p className="text-[11px] text-[var(--color-text-sub)]">
            콘솔 로그인: <span className="font-semibold text-[var(--color-text)]">norigonggan</span>{' '}
            <span className="text-[10px]">(구글)</span>
          </p>
        </div>
      ) : null}

      {/* 기준 잔액 업데이트 모달 */}
      {editing.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setEditing({ open: false, value: '' })}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md space-y-3 rounded-2xl bg-[var(--color-surface)] p-6"
          >
            <h3 className="text-lg font-bold">기준 잔액 업데이트</h3>
            <p className="text-xs text-[var(--color-text-sub)]">
              console.anthropic.com 에서 확인한 현재 잔액(USD)을 입력하세요.
              이후 누적 AI 비용이 이 값에서 차감되어 잔여로 표시됩니다.
            </p>
            <Input
              type="number"
              step="0.01"
              min={0}
              placeholder="22.15"
              value={editing.value}
              onChange={(e) => setEditing((s) => ({ ...s, value: e.target.value }))}
            />
            {updateMut.isError && (
              <p className="text-xs text-[var(--color-fall)]">
                저장 실패 — {(updateMut.error as Error).message}
              </p>
            )}
            <div className="flex gap-2 pt-1">
              <Button
                variant="ghost"
                fullWidth
                onClick={() => setEditing({ open: false, value: '' })}
              >
                취소
              </Button>
              <Button
                fullWidth
                loading={updateMut.isPending}
                disabled={!editing.value || Number(editing.value) < 0}
                onClick={() => updateMut.mutate(Number(editing.value))}
              >
                저장
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function ChartCard({
  title,
  data,
  dataKey,
  color,
}: {
  title: string;
  data: Array<Record<string, unknown>>;
  dataKey: string;
  color: string;
}) {
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <h3 className="mb-3 text-sm font-semibold">{title}</h3>
      <div style={{ width: '100%', height: 220 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip
              contentStyle={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                fontSize: 12,
              }}
            />
            <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
